/**
 * Thin wrappers around better-auth's HTTP endpoints. We hit the routes directly
 * rather than pulling in better-auth's client SDK — the API is small and the SDK
 * adds bundle weight we don't need for a couple of screens. Browser-only
 * (credentials: 'include' sends the session cookie).
 */

export type AuthError = {
  message: string;
  code?: string;
};

export type AuthResult<T> = { ok: true; data: T } | { ok: false; error: AuthError };

async function postJson<T>(path: string, body: unknown): Promise<AuthResult<T>> {
  const res = await fetch(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(body),
  });

  if (res.ok) {
    const data = (await res.json().catch(() => ({}))) as T;
    return { ok: true, data };
  }

  let message = `Request failed (${res.status})`;
  let code: string | undefined;
  try {
    const errBody = (await res.json()) as { message?: string; code?: string };
    if (errBody.message) message = errBody.message;
    if (errBody.code) code = errBody.code;
  } catch {
    // non-JSON error body — keep the default message
  }
  return { ok: false, error: { message, code } };
}

export function signInWithEmail(input: { email: string; password: string }) {
  return postJson<{ user: { id: string; email: string } }>('/api/auth/sign-in/email', input);
}

export function signUpWithEmail(input: { email: string; password: string; name: string }) {
  return postJson<{ user: { id: string; email: string } }>('/api/auth/sign-up/email', input);
}

export function signOut() {
  return postJson<Record<string, never>>('/api/auth/sign-out', {});
}

/**
 * Send a passwordless sign-in link (email-first, one field). Creates a `member`
 * account on first verify. This is the sole self-serve path for consumers and
 * organizers — same call for sign-up and sign-in. `callbackURL` is where the
 * emailed link lands the user once verified (e.g. the event they wanted).
 */
export function sendMagicLink(input: { email: string; callbackURL: string }) {
  return postJson<{ status: boolean }>('/api/auth/sign-in/magic-link', input);
}

/** Request a password-reset email. better-auth mails a link to redirectTo?token=… */
export function requestPasswordReset(input: { email: string; redirectTo: string }) {
  return postJson<Record<string, unknown>>('/api/auth/request-password-reset', input);
}

/** Complete a password reset with the token from the emailed link. */
export function resetPassword(input: { newPassword: string; token: string }) {
  return postJson<Record<string, unknown>>('/api/auth/reset-password', input);
}

/** Claim an admin invite: creates the account with a self-chosen password. */
export function claimInvite(token: string, input: { name: string; password: string }) {
  return postJson<{ email: string }>(`/api/invite/${token}/claim`, input);
}
